import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const V = {
  ink:   '#0c1830', ink2: '#2a3956', ink3: '#5a6680', ink4: '#8a93a6',
  paper: '#fbfaf6', paper2: '#f3f1ea', line: '#d8d3c2', gold: '#c8a64b',
  bad:   '#9c2b1a', badSoft: '#f4e8e5',
  serif: "'Source Serif 4', Georgia, serif",
  sans:  "'Inter', -apple-system, system-ui, sans-serif",
  mono:  "'JetBrains Mono', ui-monospace, monospace",
};

export default function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    if (document.getElementById('verdict-fonts')) return;
    const link = document.createElement('link');
    link.id = 'verdict-fonts';
    link.href = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600;8..60,700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await api.post('/api/auth/login', { email, password });
      login(res.data.token, res.data.user);
      navigate('/');
    } catch {
      setError('Invalid email or password. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div style={s.wrap}>
      {/* Left panel — brand */}
      <div style={s.left}>
        <div style={s.leftInner}>
          <div style={s.wordmark}>
            <span style={s.wordmarkV}>V</span>erdict
          </div>
          <div style={s.tagline}>Negotiation Intelligence</div>
          <div style={s.leftRule}></div>
          <div style={s.pitch}>
            Every quotation, contract, and amendment — compared, analysed, and briefed. The Consigliere is ready.
          </div>
          <div style={s.features}>
            {['AI-prepared comparison briefs', 'Live supplier intelligence', 'Invoice verification engine', 'Maritime & construction ready'].map((f, i) => (
              <div key={i} style={s.featureItem}>
                <div style={s.featureDot}></div>
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={s.leftFooter}>verdicts.eu · Negotiation Intelligence Platform</div>
      </div>

      {/* Right panel — login form */}
      <div style={s.right}>
        <div style={s.formBox}>
          <div style={s.formHeader}>
            <div style={s.formTitle}>Sign in to Verdict</div>
            <div style={s.formSub}>Enter your credentials to access your negotiation desk</div>
          </div>

          <form onSubmit={handleSubmit} style={s.form}>
            {error && (
              <div style={s.errorBox}>{error}</div>
            )}

            <div style={s.field}>
              <label style={s.label}>Email address</label>
              <input
                style={s.input}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="george@optima.com"
                required
                autoFocus
              />
            </div>

            <div style={s.field}>
              <label style={s.label}>Password</label>
              <input
                style={s.input}
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              style={{ ...s.btn, opacity: loading ? 0.7 : 1 }}
              disabled={loading}
            >
              {loading ? 'Signing in…' : 'Sign in to Verdict'}
            </button>
          </form>

          <div style={s.formFooter}>
            Verdict · Negotiation Intelligence Platform<br />
            <span style={{ color: V.ink4 }}>For access, contact your account administrator</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  wrap:         { minHeight: '100vh', display: 'flex', fontFamily: V.sans },

  // Left brand panel
  left:         { width: '42%', background: V.ink, display: 'flex', flexDirection: 'column', padding: '48px 52px', position: 'relative' },
  leftInner:    { flex: 1, display: 'flex', flexDirection: 'column' },
  wordmark:     { fontFamily: V.serif, fontSize: 42, fontWeight: 600, color: V.paper, letterSpacing: -1, lineHeight: 1, marginBottom: 8 },
  wordmarkV:    { color: V.gold },
  tagline:      { fontSize: 11, fontWeight: 500, letterSpacing: 3, textTransform: 'uppercase', color: V.ink3, marginBottom: 40 },
  leftRule:     { width: 40, height: 2, background: V.gold, marginBottom: 32 },
  pitch:        { fontFamily: V.serif, fontSize: 18, fontWeight: 400, color: 'rgba(251,250,246,0.75)', lineHeight: 1.6, marginBottom: 40, maxWidth: 360 },
  features:     { display: 'flex', flexDirection: 'column', gap: 14 },
  featureItem:  { display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: 'rgba(251,250,246,0.55)', fontFamily: V.sans },
  featureDot:   { width: 6, height: 6, borderRadius: '50%', background: V.gold, flexShrink: 0 },
  leftFooter:   { fontSize: 10, color: V.ink3, letterSpacing: 0.5, fontFamily: V.mono },

  // Right form panel
  right:        { flex: 1, background: V.paper2, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px' },
  formBox:      { width: '100%', maxWidth: 420 },
  formHeader:   { marginBottom: 32 },
  formTitle:    { fontFamily: V.serif, fontSize: 26, fontWeight: 500, color: V.ink, marginBottom: 6 },
  formSub:      { fontSize: 13, color: V.ink4 },
  form:         { display: 'flex', flexDirection: 'column', gap: 20 },
  field:        { display: 'flex', flexDirection: 'column', gap: 6 },
  label:        { fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink3 },
  input:        { background: V.paper, border: `1px solid ${V.line}`, borderRadius: 4, padding: '11px 14px', fontSize: 14, color: V.ink, fontFamily: V.sans, outline: 'none', transition: 'border-color 0.15s' },
  btn:          { background: V.gold, color: V.ink, border: 'none', borderRadius: 4, padding: '13px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: V.sans, letterSpacing: 0.2, marginTop: 4, transition: 'opacity 0.15s' },
  errorBox:     { background: V.badSoft, border: `1px solid ${V.bad}`, borderRadius: 4, padding: '11px 14px', fontSize: 13, color: V.bad },
  formFooter:   { marginTop: 32, fontSize: 11, color: V.ink3, lineHeight: 1.6, textAlign: 'center' },
};
